import { createSlice } from '@reduxjs/toolkit'

const mapProviderList = list => {
  if (!list) {
    return []
  }
  const providers = []
  for (const [, provider] of Object.entries(list)) {
    const data = {
      label: provider.name,
      uuid: provider.uuid,
    }
    providers.push(data)
  }
  return providers
}

export const initialState = {
  initialized: false,
  save: {
    loading: false,
  },
  errors: {},
  general: {
    providersList: [],
    title: '',
  },
  routes: {
    providerInformation: '',
    store: '',
    update: '',
    delete: '',
  },
  selectedProvider: {
    uuid: '',
    apiIntegration: 0,
  },
  selectedOffer: {
    id: 0,
  },
  viewParameters: {
    parametersLoaded: false,
  },
  comparators: [],
}

const slice = createSlice({
  name: 'organizationsManagement',
  initialState,
  reducers: {
    initialize: (state, { payload }) => {
      state.initialized = true
      state.routes = { ...(payload.routesSpa ?? {}) }
      state.general = {
        providersList: mapProviderList(payload.organisations ?? []),
      }
    },
    setViewParameters: (state, { payload }) => {
      state.viewParameters = {
        parametersLoaded: true,
        ...(payload ?? {}),
      }
    },
    setProvider: (state, { payload }) => {
      const mandatoryOperationsRaw = payload?.italy_identity_document_mandatory_operations
      let mandatoryOperations = []
      if (Array.isArray(mandatoryOperationsRaw)) {
        mandatoryOperations = mandatoryOperationsRaw
      } else if (typeof mandatoryOperationsRaw === 'string') {
        try {
          mandatoryOperations = JSON.parse(mandatoryOperationsRaw)
        } catch {
          mandatoryOperations = []
        }
      }
      state.selectedProvider = {
        ...(payload ?? {}),
        apiIntegration: payload.api_workflow && payload.api_workflow.length > 0 ? 1 : 0,
        identity_mandatory_cdf: mandatoryOperations.includes('cdf') ? 1 : 0,
        identity_mandatory_change_transferred: mandatoryOperations.includes('change_transferred') ? 1 : 0,
        identity_mandatory_mes: mandatoryOperations.includes('mes') ? 1 : 0,
        identity_mandatory_fmes: mandatoryOperations.includes('fmes') ? 1 : 0,
        identity_mandatory_new_connection: mandatoryOperations.includes('new_connection') ? 1 : 0,
      }
    },
    updateProvider: (state, { payload }) => {
      Object.assign(state.selectedProvider, payload ?? {})
    },
    setErrors: (state, { payload }) => {
      state.errors = { ...(payload ?? {}) }
    },
    saving: (state, { payload }) => {
      Object.assign(state.save, payload ?? {})
    },
    reset: state => {
      state.selectedProvider = {
        uuid: '',
      }
    },
    setOffer: (state, { payload }) => {
      state.selectedOffer = { ...(payload ?? {}) }
    },
    updateOffer: (state, { payload }) => {
      Object.assign(state.selectedOffer, payload ?? {})
    },
    setOfferCutPeriods: (state, { payload }) => {
      state.selectedOffer.cut_periods = payload ?? []
    },
    addOfferCutPeriod: (state, { payload }) => {
      const list = Array.isArray(state.selectedOffer.cut_periods) ? state.selectedOffer.cut_periods : []
      state.selectedOffer.cut_periods = [...list, payload]
    },
    updateOfferCutPeriod: (state, { payload }) => {
      const list = Array.isArray(state.selectedOffer.cut_periods) ? state.selectedOffer.cut_periods : []
      state.selectedOffer.cut_periods = list.map(cutPeriod =>
        parseInt(cutPeriod.id) === parseInt(payload.id) ? { ...cutPeriod, ...payload } : cutPeriod,
      )
    },
    removeOfferCutPeriod: (state, { payload: cutPeriodId }) => {
      const list = Array.isArray(state.selectedOffer.cut_periods) ? state.selectedOffer.cut_periods : []
      state.selectedOffer.cut_periods = list.filter(cutPeriod => parseInt(cutPeriod.id) !== parseInt(cutPeriodId))
    },
    resetOffer: state => {
      state.selectedOffer = {
        uuid: '',
        active: true,
      }
    },
    setComparators: (state, { payload }) => {
      state.comparators = payload ?? []
    },
    addComparator: (state, { payload }) => {
      state.comparators.push(payload)
    },
    removeProvider: (state, { payload: providerId }) => {
      state.general.providersList = state.general.providersList.filter(p => p.uuid !== providerId)
      if (state.selectedProvider.uuid === providerId) {
        state.selectedProvider = { uuid: '', apiIntegration: 0 }
      }
    },
  },
})

export const {
  initialize,
  setViewParameters,
  setProvider,
  updateProvider,
  setErrors,
  saving,
  reset,
  setOffer,
  updateOffer,
  resetOffer,
  setComparators,
  addComparator,
  removeProvider,
  setOfferCutPeriods,
  addOfferCutPeriod,
  updateOfferCutPeriod,
  removeOfferCutPeriod,
} = slice.actions

export default slice.reducer
